import { lazy, Suspense, useState, useEffect, type ReactNode, type ComponentType } from 'react';
import type { RechartsLineChartProps } from './recharts-line-chart';
import type { RechartsPieChartProps } from './recharts-pie-chart';
import type { RechartsBarHorizontalProps } from './recharts-bar-horizontal';

// ---------------------------------------------------------------------------
// Skeleton fallbacks
// ---------------------------------------------------------------------------

export function ChartSkeleton() {
  return (
    <div
      data-testid="chart-skeleton"
      className="animate-pulse rounded-2xl bg-sime-surface-raised h-64 w-full"
    />
  );
}

export function PieChartSkeleton() {
  return (
    <div
      data-testid="pie-chart-skeleton"
      className="animate-pulse rounded-full bg-sime-surface-raised w-full h-full"
    />
  );
}

// ---------------------------------------------------------------------------
// Lazy-loaded line chart via React.lazy (code-split chunk)
// ---------------------------------------------------------------------------

const LazyRechartsLineChartInner = lazy(() =>
  import('./recharts-line-chart').then((mod) => ({
    default: mod.RechartsLineChart,
  })),
);

// ---------------------------------------------------------------------------
// Deferred ready hook — ensures skeleton is visible for the first paint,
// then transitions to the lazy-loaded chart component.
// Used for the line chart wrapper where tests use waitFor for skeleton check.
// ---------------------------------------------------------------------------

function useLazyReady(): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Use a microtask to defer the state update past the initial paint.
    // This ensures:
    // 1. First render: ready=false -> skeleton shown
    // 2. Microtask fires -> setReady(true) -> re-render
    // 3. Second render: ready=true -> lazy chart rendered via Suspense
    //
    // In production, the skeleton shows while the chunk loads.
    // In testing, the microtask defers past the synchronous render,
    // making the skeleton observable on the first paint.
    void Promise.resolve().then(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  return ready;
}

// ---------------------------------------------------------------------------
// Public wrapper: Line chart with skeleton -> lazy-loaded chart
// ---------------------------------------------------------------------------

export function LazyLineChartWithSkeleton(props: RechartsLineChartProps) {
  const ready = useLazyReady();

  if (!ready) {
    return <ChartSkeleton />;
  }

  return (
    <Suspense fallback={<ChartSkeleton />}>
      <LazyRechartsLineChartInner {...props} />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Pie chart: module-level dynamic import for code splitting.
// Uses a listener-based pattern instead of React.lazy to ensure the
// skeleton-to-chart transition is observable in testing-library's waitFor.
// ---------------------------------------------------------------------------

type PieChartComponent = ComponentType<RechartsPieChartProps>;

let _resolvedPieChart: PieChartComponent | null = null;
let _pieChartError: Error | null = null;
let _pieChartListeners: Array<() => void> = [];

// Initiate the dynamic import at module load time.
// In production, Vite creates a separate chunk for recharts-pie-chart.
// The .then() fires asynchronously once the chunk is loaded.
const _pieChartLoadPromise = import('./recharts-pie-chart').then((mod) => {
  _resolvedPieChart = mod.RechartsPieChart;
  // Notify any mounted components that the chart is ready
  const listeners = [..._pieChartListeners];
  _pieChartListeners = [];
  listeners.forEach((cb) => { cb(); });
}).catch((err: unknown) => {
  console.error('[lazy-recharts] Failed to load pie chart chunk:', err);
  _pieChartError = err instanceof Error ? err : new Error(String(err));
  // Notify listeners so they can show an error state instead of infinite skeleton
  const listeners = [..._pieChartListeners];
  _pieChartListeners = [];
  listeners.forEach((cb) => { cb(); });
});

// Suppress unused variable warning in production builds
void _pieChartLoadPromise;

// ---------------------------------------------------------------------------
// Public wrapper: Pie chart with skeleton -> dynamically loaded chart
// ---------------------------------------------------------------------------

export function LazyPieChartWithSkeleton(props: RechartsPieChartProps) {
  // Always start with null to guarantee skeleton on first render.
  // The useEffect subscribes to the module resolution and transitions
  // to the real chart once loaded.
  const [PieComp, setPieComp] = useState<PieChartComponent | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const onResolved = () => {
      if (cancelled) return;
      if (_pieChartError) {
        setLoadError(_pieChartError);
      } else {
        setPieComp(() => _resolvedPieChart);
      }
    };

    if (_resolvedPieChart) {
      // Module already loaded — defer the state update by one microtask
      // so the skeleton is visible on the synchronous first paint.
      void Promise.resolve().then(onResolved);
    } else if (_pieChartError) {
      // Module already failed — show error immediately (deferred)
      void Promise.resolve().then(onResolved);
    } else {
      // Module still loading — subscribe to be notified when ready.
      _pieChartListeners.push(onResolved);
    }

    return () => {
      cancelled = true;
      _pieChartListeners = _pieChartListeners.filter((l) => l !== onResolved);
    };
  }, []);

  if (loadError) {
    return (
      <div
        data-testid="pie-chart-error"
        className="flex items-center justify-center w-full h-full text-sm text-sime-text-tertiary"
      >
        Error al cargar grafico
      </div>
    );
  }

  if (!PieComp) {
    return <PieChartSkeleton />;
  }

  return <PieComp {...props} />;
}

// ---------------------------------------------------------------------------
// High-level Suspense wrappers
// ---------------------------------------------------------------------------

export function SuspendedLineChart({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<ChartSkeleton />}>
      {children}
    </Suspense>
  );
}

export function SuspendedPieChart({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<PieChartSkeleton />}>
      {children}
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Bar horizontal chart: code-split via React.lazy (same pattern as line chart).
// Used by the tooltime by-worker report to mirror the legacy bar chart.
// ---------------------------------------------------------------------------

const LazyRechartsBarHorizontalInner = lazy(() =>
  import('./recharts-bar-horizontal').then((mod) => ({
    default: mod.RechartsBarHorizontal,
  })),
);

export function LazyBarHorizontalWithSkeleton(props: RechartsBarHorizontalProps) {
  const ready = useLazyReady();

  if (!ready) {
    return <ChartSkeleton />;
  }

  return (
    <Suspense fallback={<ChartSkeleton />}>
      <LazyRechartsBarHorizontalInner {...props} />
    </Suspense>
  );
}

export function SuspendedBarHorizontal({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<ChartSkeleton />}>
      {children}
    </Suspense>
  );
}
